import { useCallback, useMemo } from 'react';

import { FlexAligner, Select, type SelectOption } from '@scaleaq/scaleui';
import { useTranslation } from 'react-i18next';

import { buildEnumSelectOptions } from '@/components/biomassDistribution/biomassDistribution.utils.ts';
import { ChipsItem, SegmentedChips } from '@/components/chips';
import { FieldWrapper } from '@/components/inputFields';
import { useBiomassDistributionPreferencesStorage } from '@/stores';
import { responsive } from '@/utils';

import {
  BiomassDistributionBinSize,
  BiomassDistributionInterval,
  BiomassDistributionView,
} from './biomassDistribution.types.ts';

type BiomassDistributionToolbarProps = {
  widgetId: string;
};

export const BiomassDistributionToolbar = ({ widgetId }: BiomassDistributionToolbarProps) => {
  const { t } = useTranslation(['orbitOne']);
  const { interval, binSize, view, setInterval, setBinSize, setView } =
    useBiomassDistributionPreferencesStorage(widgetId);

  const intervalOptions = useMemo(
    () =>
      buildEnumSelectOptions(BiomassDistributionInterval, key =>
        t(`orbitOne:biomassDistribution.config.intervalOptions.${key}`)
      ),
    [t]
  );

  const binSizeOptions = useMemo(
    () =>
      buildEnumSelectOptions(BiomassDistributionBinSize, key =>
        t(`orbitOne:biomassDistribution.config.binSizeOptions.${key}`)
      ),
    [t]
  );

  const viewItems = useMemo<ChipsItem<BiomassDistributionView>[]>(
    () => [
      {
        id: BiomassDistributionView.Chart,
        label: t('orbitOne:biomassDistribution.view.chart'),
        value: BiomassDistributionView.Chart,
      },
      {
        id: BiomassDistributionView.Table,
        label: t('orbitOne:biomassDistribution.view.table'),
        value: BiomassDistributionView.Table,
      },
    ],
    [t]
  );

  const selectedIntervalOption =
    intervalOptions.find(option => option.value === interval) ?? intervalOptions[0];
  const selectedBinSizeOption =
    binSizeOptions.find(option => option.value === binSize) ?? binSizeOptions[0];
  const selectedViewItem = viewItems.find(item => item.value === view) ?? viewItems[0]!;

  const handleIntervalChange = useCallback(
    (option: SelectOption<BiomassDistributionInterval> | null) => {
      if (option?.value) setInterval(option.value);
    },
    [setInterval]
  );
  const handleBinSizeChange = useCallback(
    (option: SelectOption<BiomassDistributionBinSize> | null) => {
      if (option?.value) setBinSize(option.value);
    },
    [setBinSize]
  );
  const handleViewChange = useCallback(
    (item: ChipsItem<BiomassDistributionView>) => {
      if (item.value) setView(item.value);
    },
    [setView]
  );

  return (
    <FlexAligner alignment='bottom-left' gap={responsive.spacing.medium}>
      <SegmentedChips
        size='small'
        radiusRules='square'
        items={viewItems}
        value={selectedViewItem}
        onChange={handleViewChange}
      />
      <FieldWrapper $maxWidth='fit-content'>
        <Select
          allowGrow
          placeholder={t('orbitOne:biomassDistribution.config.binSize')}
          options={binSizeOptions}
          value={selectedBinSizeOption}
          onChange={handleBinSizeChange}
          isSearchable={false}
        />
      </FieldWrapper>
      <FieldWrapper $maxWidth='fit-content'>
        <Select
          allowGrow
          placeholder={t('orbitOne:biomassDistribution.config.interval')}
          options={intervalOptions}
          value={selectedIntervalOption}
          onChange={handleIntervalChange}
          isSearchable={false}
        />
      </FieldWrapper>
    </FlexAligner>
  );
};
